import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtomValue, useStore } from 'jotai'
import type { SseRequestError, SseStep } from '@/app/services/http/sseClient'
import {
  applyStep,
  crefisaProgressFamily,
  currentStep,
  emptyProgress,
} from '@/app/store/crefisaProgress'

export interface CrefisaStreamContext {
  onStep: (step: SseStep) => void
  signal: AbortSignal
}

export interface UseCrefisaStreamOptions<T> {
  queryKey: unknown[]
  enabled?: boolean
  request: (context: CrefisaStreamContext) => Promise<T>
}

export function useCrefisaStream<T>({
  queryKey,
  enabled = true,
  request,
}: UseCrefisaStreamOptions<T>) {
  const store = useStore()
  const queryClient = useQueryClient()

  const progressAtom = useMemo(
    () => crefisaProgressFamily(JSON.stringify(queryKey)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(queryKey)],
  )

  const progress = useAtomValue(progressAtom)

  const controllerRef = useRef<AbortController | null>(null)

  const query = useQuery<T, SseRequestError>({
    queryKey,
    enabled,
    staleTime: Infinity,
    queryFn: () => {
      store.set(progressAtom, emptyProgress)

      const controller = new AbortController()
      controllerRef.current = controller

      return request({
        signal: controller.signal,
        onStep: (step) =>
          store.set(progressAtom, (previous) => applyStep(previous, step)),
      })
    },
  })

  const cancel = useCallback(() => {
    controllerRef.current?.abort()
    queryClient.cancelQueries({ queryKey })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, JSON.stringify(queryKey)])

  const retry = useCallback(() => {
    store.set(progressAtom, emptyProgress)
    return query.refetch()
  }, [store, progressAtom, query])

  return {
    data: query.data,
    steps: progress.steps,
    currentStep: currentStep(progress),
    isStreaming: query.isFetching,
    isPending: query.isFetching && !query.data,
    isError: query.isError,
    error: query.error,
    cancel,
    retry,
  }
}

export function useCrefisaAction<TArgs, TResult>(
  action: (args: TArgs, context: CrefisaStreamContext) => Promise<TResult>,
) {
  const [steps, setSteps] = useState<SseStep[]>([])
  const controllerRef = useRef<AbortController | null>(null)
  const actionRef = useRef(action)
  actionRef.current = action

  const mutation = useMutation<TResult, SseRequestError, TArgs>({
    mutationFn: (args) => {
      setSteps([])

      const controller = new AbortController()
      controllerRef.current = controller

      return actionRef.current(args, {
        signal: controller.signal,
        onStep: (step) =>
          setSteps((previous) => applyStep({ steps: previous }, step).steps),
      })
    },
  })

  const cancel = useCallback(() => {
    controllerRef.current?.abort()
  }, [])

  const reset = useCallback(() => {
    setSteps([])
    mutation.reset()
  }, [mutation])

  return {
    run: mutation.mutateAsync,
    steps,
    currentStep: currentStep({ steps }),
    isRunning: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    cancel,
    reset,
  }
}
